import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Volume2, X, Trash2, Moon, Sun } from 'lucide-react';


const LANGUAGES = [
  // 🌍 Global
  { code: 'en', name: 'English', voiceCode: 'en-US' },
  { code: 'es', name: 'Spanish', voiceCode: 'es-ES' },
  { code: 'fr', name: 'French', voiceCode: 'fr-FR' },
  { code: 'de', name: 'German', voiceCode: 'de-DE' },
  { code: 'pt', name: 'Portuguese', voiceCode: 'pt-BR' },
  { code: 'ru', name: 'Russian', voiceCode: 'ru-RU' },
  { code: 'ja', name: 'Japanese', voiceCode: 'ja-JP' },
  { code: 'ko', name: 'Korean', voiceCode: 'ko-KR' },
  { code: 'zh-CN', name: 'Mandarin', voiceCode: 'zh-CN' },
  // 🇮🇳 India-focused
  { code: 'hi', name: 'Hindi', voiceCode: 'hi-IN' },
  { code: 'mr', name: 'Marathi', voiceCode: 'mr-IN' },
  { code: 'ta', name: 'Tamil', voiceCode: 'ta-IN' },
  { code: 'te', name: 'Telugu', voiceCode: 'te-IN' },
  { code: 'kn', name: 'Kannada', voiceCode: 'kn-IN' },
  { code: 'ml', name: 'Malayalam', voiceCode: 'ml-IN' },
  { code: 'bn', name: 'Bengali', voiceCode: 'bn-IN' },
  { code: 'gu', name: 'Gujarati', voiceCode: 'gu-IN' },
  { code: 'pa', name: 'Punjabi', voiceCode: 'pa-IN' },
  { code: 'ur', name: 'Urdu', voiceCode: 'ur-IN' },
];


function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [lang1, setLang1] = useState('en');
  const [lang2, setLang2] = useState('hi'); // changed default to Hindi–English pair
  const [isListening, setIsListening] = useState(false);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [liveText, setLiveText] = useState('');
  const [liveTranslation, setLiveTranslation] = useState('');
  const [apiKey] = useState('AIzaSyCE475doHC56yM5hu1vDA0l5lQ9UZS4M20'); // ← your key is here
  const [showSettings, setShowSettings] = useState(false);

  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const debounceTimerRef = useRef(null);
  const activeSpeakerDuringSessionRef = useRef(null);
  const silenceTimerRef = useRef(null);


  const translate = useCallback(async (text, sourceLang, targetLang) => {
    if (!text?.trim()) return text;

    try {
      const response = await fetch(
        `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            q: text,
            source: sourceLang,
            target: targetLang,
            format: 'text',
          }),
        }
      );

      if (!response.ok) {
        console.warn(`Translation failed: ${response.status}`);
        return text;
      }

      const { data } = await response.json();
      return data.translations?.[0]?.translatedText || text;
    } catch (err) {
      console.error('Translation error:', err);
      return text;
    }
  }, [apiKey]);

  const speak = useCallback((text, langCode) => {
    if (!text) return;
    const voiceCode = LANGUAGES.find(l => l.code === langCode)?.voiceCode || 'en-US';
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = voiceCode;
    utterance.rate = 0.92;
    window.speechSynthesis.speak(utterance);
  }, []);

  const saveMessage = useCallback(async (speaker, text) => {
    if (!text?.trim()) return;

    const fromLang = speaker === 1 ? lang1 : lang2;
    const toLang   = speaker === 1 ? lang2 : lang1;

    console.log(`→ Saving: "${text}" (${fromLang} → ${toLang})`);

    const translated = await translate(text.trim(), fromLang, toLang);

    const message = {
      id: Date.now(),
      speaker,
      original: text.trim(),
      translated,
      fromLang,
      toLang,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setConversation(prev => [...prev, message]);
    speak(translated, toLang);

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
  }, [lang1, lang2, translate, speak]);

  const initRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser doesn't support speech recognition. Try Chrome.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = '';
      let final = finalTranscriptRef.current || '';

      // AUTO STOP ON SILENCE
if (silenceTimerRef.current) {
  clearTimeout(silenceTimerRef.current);
}

silenceTimerRef.current = setTimeout(() => {
  console.log('Auto-stopping due to silence');
  recognition.stop();
}, 1200); // 1.2 seconds of silence


      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0]?.transcript?.trim() || '';
        if (event.results[i].isFinal) {
          final = transcript;
          console.log('Final result:', transcript);
        } else {
          interim = transcript;
        }
      }

      const current = final || interim;
      setLiveText(current);

      if (final) finalTranscriptRef.current = final;

      // Debounced live translation
      if (current.length > 2 && activeSpeaker) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
          const from = activeSpeaker === 1 ? lang1 : lang2;
          const to   = activeSpeaker === 1 ? lang2 : lang1;
          const translated = await translate(current, from, to);
          setLiveTranslation(translated);
        }, 300);
      }
    };

    recognition.onend = async () => {
  await new Promise(r => setTimeout(r, 200));

  let textToSave = (finalTranscriptRef.current || liveText || '').trim();

  const currentSpeaker = activeSpeakerDuringSessionRef.current;

  console.log('=== ONEND DEBUG ===', {
    finalRef: finalTranscriptRef.current,
    liveText,
    textToSave,
    speakerFromRef: currentSpeaker,           // ← this should now be 1 or 2
    lang: currentSpeaker === 1 ? lang1 : lang2
  });

  if (textToSave && currentSpeaker) {
    console.log('Attempting to save:', textToSave);
    await saveMessage(currentSpeaker, textToSave);
  } else {
    console.log('Skipped saving – no text or no speaker');
  }

  if (silenceTimerRef.current) {
  clearTimeout(silenceTimerRef.current);
  silenceTimerRef.current = null;
}

  // Cleanup
  setIsListening(false);
  setActiveSpeaker(null);
  activeSpeakerDuringSessionRef.current = null;  // reset ref
  setLiveText('');
  setLiveTranslation('');
  finalTranscriptRef.current = '';
  if (debounceTimerRef.current) {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
  }
};

    recognition.onerror = (event) => {
      console.error('Recognition error:', event.error);
      setIsListening(false);
      setActiveSpeaker(null);
    };
  }, [activeSpeaker, lang1, lang2, liveText, translate, saveMessage]);

  useEffect(() => {
    initRecognition();
    return () => recognitionRef.current?.stop();
  }, [initRecognition]);

  const toggleListening = (speaker) => {
  const rec = recognitionRef.current;
  if (!rec) return;

  if (isListening && activeSpeaker === speaker) {
  if (silenceTimerRef.current) {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
  }
  rec.stop();
  return;
}

  if (isListening) return;

  const lang = speaker === 1 ? lang1 : lang2;
  const voiceCode = LANGUAGES.find(l => l.code === lang)?.voiceCode || 'en-US';

  rec.lang = voiceCode;
  setActiveSpeaker(speaker);
  activeSpeakerDuringSessionRef.current = speaker;  // ← remember it here
  setIsListening(true);
  setLiveText('');
  setLiveTranslation('');
  finalTranscriptRef.current = '';

  try {
    rec.start();
    console.log(`Started listening for speaker ${speaker} (${lang})`);
  } catch (err) {
    console.error('Start failed:', err);
    setIsListening(false);
    setActiveSpeaker(null);
    activeSpeakerDuringSessionRef.current = null;
  }
};

  const clearConversation = () => {
    setConversation([]);
    window.speechSynthesis.cancel();
  };

  const lang1Name = LANGUAGES.find(l => l.code === lang1)?.name || 'Person 1';
  const lang2Name = LANGUAGES.find(l => l.code === lang2)?.name || 'Person 2';

  return (
    <div className={`flex flex-col h-screen transition-colors duration-200 ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
     <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b shadow-sm sticky top-0 z-10 transition-colors duration-200`}>
  <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
    <h1 className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      ConvoBridge
    </h1>

    <div className="flex gap-2 sm:gap-3 items-center">
      <button
        onClick={clearConversation}
        className={`p-2 rounded-full transition-colors duration-150 ${
          darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
        }`}
        title="Clear conversation"
      >
        <Trash2 size={20} />
      </button>
      <button
        onClick={() => setDarkMode(!darkMode)}
        className={`p-2 rounded-full transition-colors duration-150 ${
          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
        title="Toggle theme"
      >
        {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-gray-600" />}
      </button>
    </div>
  </div>
</header>

      <div className={`border-b transition-colors duration-200 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <select
              value={lang1}
              onChange={e => setLang1(e.target.value)}
              disabled={isListening}
              className={`flex-1 p-2 sm:p-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-400' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>

            <span className={`text-xl font-light ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>↔</span>

            <select
              value={lang2}
              onChange={e => setLang2(e.target.value)}
              disabled={isListening}
              className={`flex-1 p-2 sm:p-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-green-500 transition-colors duration-200 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-green-400' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {isListening && (
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-5 w-full">
          <div className={`bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-4 sm:p-6 shadow-lg ${
            darkMode ? 'from-indigo-700 to-purple-700' : ''
          }`}>
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <span className="text-sm sm:text-base font-semibold truncate">
                {activeSpeaker === 1 ? lang1Name : lang2Name} speaking...
              </span>
              <div className="flex items-center gap-2 ml-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full animate-pulse" />
                <span className="text-xs sm:text-sm opacity-80">Live</span>
              </div>
            </div>
            <p className="text-base sm:text-lg mb-3 sm:mb-4 min-h-[1.5rem] break-words">{liveText || 'Listening...'}</p>
            <div className="pt-3 sm:pt-4 border-t border-white/30">
              <p className="text-sm sm:text-base opacity-95 min-h-[1.5rem] break-words">
                → {liveTranslation || (liveText ? 'Translating...' : '')}
              </p>
            </div>
          </div>
        </div>
      )}

      <main className={`flex-1 overflow-y-auto p-3 sm:p-4 pb-40 sm:pb-32 transition-colors duration-200 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-100'
      }`}>
        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-5">
          {conversation.length === 0 ? (
            <div className={`text-center py-16 sm:py-20 rounded-lg ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <Mic size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-base sm:text-lg">Press a microphone to start speaking</p>
            </div>
          ) : (
            conversation.map(msg => (
              <div key={msg.id} className={`flex ${msg.speaker === 1 ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] sm:max-w-[70%] md:max-w-[60%] rounded-2xl shadow-md overflow-hidden transition-all duration-200 ${
                  msg.speaker === 1 
                    ? darkMode ? 'bg-gray-800 border border-gray-700 text-gray-100' : 'bg-gradient-to-br from-blue-50 to-white border border-blue-100 text-gray-900'
                    : darkMode ? 'bg-emerald-600 border border-emerald-500 text-white' : 'bg-gradient-to-br from-emerald-500 to-emerald-600 border border-emerald-400 text-white'
                }`}>
                  <div className={`p-3 sm:p-4 transition-colors duration-200 ${
                    msg.speaker === 1 
                      ? darkMode ? 'bg-gray-700 border-b border-gray-600' : 'bg-blue-100 border-b border-blue-200'
                      : darkMode ? 'bg-emerald-700 border-b border-emerald-600' : 'bg-emerald-600 border-b border-emerald-500'
                  }`}>
                    <div className="flex justify-between items-center mb-2 gap-2">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${
                        msg.speaker === 1
                          ? darkMode ? 'text-gray-300' : 'text-blue-700'
                          : 'text-white opacity-90'
                      }`}>
                        {msg.speaker === 1 ? lang1Name : lang2Name}
                      </span>
                      <button onClick={() => speak(msg.original, msg.fromLang)} className={`p-1 rounded hover:opacity-70 transition-all ${
                        msg.speaker === 1
                          ? darkMode ? 'hover:bg-gray-600' : 'hover:bg-blue-200'
                          : 'hover:bg-white/20'
                      }`}>
                        <Volume2 size={16} />
                      </button>
                    </div>
                    <p className={`text-sm sm:text-base leading-relaxed font-medium ${
                      msg.speaker === 1
                        ? darkMode ? 'text-gray-50' : 'text-gray-900'
                        : 'text-white'
                    }`}>{msg.original}</p>
                  </div>

                  <div className={`p-3 sm:p-4 border-t transition-colors duration-200 ${
                    msg.speaker === 1
                      ? darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-100'
                      : darkMode ? 'bg-emerald-600 border-emerald-600' : 'bg-emerald-500 border-emerald-500'
                  }`}>
                    <div className="flex justify-between items-center mb-2 gap-2">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${
                        msg.speaker === 1
                          ? darkMode ? 'text-gray-400' : 'text-blue-600'
                          : 'text-white/80'
                      }`}>
                        {msg.speaker === 1 ? lang2Name : lang1Name} translation
                      </span>
                      <button onClick={() => speak(msg.translated, msg.toLang)} className={`p-1 rounded hover:opacity-70 transition-all ${
                        msg.speaker === 1
                          ? darkMode ? 'hover:bg-gray-600' : 'hover:bg-blue-200'
                          : 'hover:bg-white/20'
                      }`}>
                        <Volume2 size={16} />
                      </button>
                    </div>
                    <p className={`text-sm sm:text-base leading-relaxed ${
                      msg.speaker === 1
                        ? darkMode ? 'text-gray-200' : 'text-gray-700'
                        : 'text-white/95'
                    }`}>{msg.translated}</p>
                  </div>

                  <div className={`px-3 sm:px-4 py-2 text-xs font-medium transition-colors duration-200 ${
                    msg.speaker === 1 
                      ? darkMode ? 'bg-gray-700 text-gray-400 border-t border-gray-600' : 'bg-blue-50 text-gray-500 border-t border-blue-100'
                      : darkMode ? 'bg-emerald-700 text-emerald-200 border-t border-emerald-600' : 'bg-emerald-700 text-white/70 border-t border-emerald-600'
                  }`}>
                    <div className="text-right">{msg.time}</div>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>
      </main>

      <footer className={`border-t sticky bottom-0 z-10 shadow-lg transition-colors duration-200 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-5 flex gap-3 sm:gap-4">
          <button
            onClick={() => toggleListening(1)}
            disabled={isListening && activeSpeaker === 2}
            className={`flex-1 py-4 sm:py-6 rounded-2xl font-medium text-white transition-all shadow-md ${
              isListening && activeSpeaker === 1 ? 'bg-red-600 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'
            } ${isListening && activeSpeaker === 2 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex flex-col items-center gap-1 sm:gap-2">
              <Mic size={24} className="sm:w-7 sm:h-7" />
              <span className="text-xs sm:text-sm">{isListening && activeSpeaker === 1 ? 'Stop' : lang1Name}</span>
            </div>
          </button>

          <button
            onClick={() => toggleListening(2)}
            disabled={isListening && activeSpeaker === 1}
            className={`flex-1 py-4 sm:py-6 rounded-2xl font-medium text-white transition-all shadow-md ${
              isListening && activeSpeaker === 2 ? 'bg-red-600 animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700'
            } ${isListening && activeSpeaker === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex flex-col items-center gap-1 sm:gap-2">
              <Mic size={24} className="sm:w-7 sm:h-7" />
              <span className="text-xs sm:text-sm">{isListening && activeSpeaker === 2 ? 'Stop' : lang2Name}</span>
            </div>
          </button>
        </div>
      </footer>

      {showSettings && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`rounded-2xl max-w-md w-full overflow-hidden shadow-2xl transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-4 sm:p-5 border-b flex justify-between items-center transition-colors duration-200 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h2 className={`text-lg sm:text-xl font-semibold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Settings</h2>
              <button onClick={() => setShowSettings(false)} className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                <X size={24} />
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <p className={`text-sm mb-4 ${
                darkMode ? 'text-green-400' : 'text-green-700'
              }`}>
                Using embedded API key – ready to translate!
              </p>
              <p className={`text-xs ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                If translation stops working later, you can generate a new key in Google Cloud Console.
              </p>
            </div>
            <div className={`p-4 sm:p-5 border-t flex justify-end transition-colors duration-200 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;